/**
 * Cart de-duplicator — merges cart lines that Shopify was forced to split apart by
 * app-injected line-item properties.
 *
 * THE BUG THIS FIXES (reproduced on production 2026-08-03):
 *   1. Customer adds a part. The line has no line-item properties.
 *   2. Customer opens /cart. The Store Pickup + Delivery (CR) app rewrites every line,
 *      stamping `_CRid` and `_deliveryMethod`. Nothing was clicked.
 *   3. Customer adds the SAME part again from any surface. That add carries no properties.
 *   4. Shopify merges two lines only when variant_id AND the full properties hash match, so
 *      it cannot merge them — the cart now shows the same part twice.
 *
 * Both `_CRid` and `_deliveryMethod` are `_`-prefixed, so snippets/cart-products.liquid
 * filters them out of the display: the two rows look identical to the shopper. The real
 * casualty was abandoned checkout 66282240606580 — someone wanting 2 oil filters ($39.60)
 * was shown 4 ($79.20) and left.
 *
 * The pickup selection the app actually needs is carried on CART ATTRIBUTES
 * (`pickup`, `Pickup_Store`, `Pickup_StoreID`), which this module never touches — merging
 * the lines does not lose the customer's pickup choice.
 *
 * Design mirrors assets/core-deposit.js deliberately:
 *   - ONE idempotent pass per cart change, mutations STRICTLY SEQUENTIAL (never concurrent)
 *     so we never trip Shopify's cart rate limit.
 *   - Every fetch FAILS OPEN. We never block add-to-cart or checkout. A miss self-heals on
 *     the next cart event.
 *   - Our own re-render dispatch is tagged so our listener ignores it (no reconcile loop).
 *
 * SAFETY: only merges lines whose properties differ SOLELY in the app-injected keys below.
 * Lines that differ in any meaningful property — notably the `_core_*` core-deposit
 * properties — are left alone, because there the split is intentional.
 */

import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { debounce } from '@theme/utilities';

/** Tag our own dispatch so our listener ignores it. */
const SOURCE = 'cart-dedup';

/** Line-item properties injected by Store Pickup + Delivery (CR). Safe to ignore when comparing. */
const APP_PROPS = ['_CRid', '_deliveryMethod'];

let running = false;

/** Cart unit count EXCLUDING theme-managed lines — core deposits (assets/core-deposit.js) and the
 *  minimum-order fee (assets/min-order-fee.js). The badge should only ever show parts the customer
 *  chose. Tests the SKU as well as the property because the CR app overwrites line properties. */
function realCount(cart) {
  return cart.items.reduce((n, line) => {
    const sku = String(line.sku || '');
    const props = line.properties || {};
    const managed =
      Boolean(props._core_deposit) ||
      Boolean(props._min_order_fee) ||
      sku.startsWith('CORE-DEP-') ||
      sku.startsWith('MIN-ORDER-FEE-');
    return managed ? n : n + line.quantity;
  }, 0);
}

/** Properties that actually distinguish one line from another. */
function significantProps(props) {
  const out = {};
  Object.keys(props || {}).forEach((k) => {
    if (!APP_PROPS.includes(k)) out[k] = props[k];
  });
  return JSON.stringify(Object.keys(out).sort().map((k) => [k, out[k]]));
}

async function getCart() {
  const res = await fetch(`${Shopify.routes.root}cart.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

/** Sequential, fail-open cart mutation. One retry on 429, then give up quietly. */
async function change(body, retry = true) {
  const res = await fetch(`${Shopify.routes.root}cart/change.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 429 && retry) {
    await new Promise((r) => setTimeout(r, 1000));
    return change(body, false);
  }
  if (!res.ok) throw new Error(`change.js ${res.status}`);
  return res.json();
}

async function reconcile() {
  if (running) return;
  running = true;
  try {
    const cart = await getCart();
    if (!cart || !Array.isArray(cart.items) || cart.items.length < 2) return;

    // Group by variant + the properties that genuinely matter.
    const groups = new Map();
    cart.items.forEach((item) => {
      const key = `${item.id}|${significantProps(item.properties)}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });

    const dupes = [...groups.values()].filter((g) => g.length > 1);
    if (dupes.length === 0) return;

    let mutated = false;
    for (const group of dupes) {
      // Keep the line that already carries the app properties, so the app stays consistent.
      const keeper =
        group.find((i) => APP_PROPS.some((p) => i.properties && i.properties[p])) || group[0];
      const total = group.reduce((sum, i) => sum + i.quantity, 0);

      for (const line of group) {
        if (line.key === keeper.key) continue;
        await change({ id: line.key, quantity: 0 }); // sequential by design
        mutated = true;
      }
      if (total !== keeper.quantity) {
        await change({ id: keeper.key, quantity: total });
        mutated = true;
      }
    }

    if (mutated) {
      // Ask the theme's cart UI to re-render. source:SOURCE so our own listener ignores it.
      const fresh = await getCart();
      document.dispatchEvent(
        // Theme-managed lines (core deposits, the minimum-order fee) must not reach the badge —
        // matches snippets/cart-display-count.liquid, core-deposit.js and min-order-fee.js.
        new CartUpdateEvent(fresh, SOURCE, { source: SOURCE, itemCount: realCount(fresh) })
      );
    }
  } catch (e) {
    // Fail open — never block the shopper. Self-heals on the next cart event.
    if (window.console && window.Shopify && Shopify.designMode) console.warn('[cart-dedup]', e);
  } finally {
    running = false;
  }
}

const onCartUpdate = debounce((event) => {
  // Ignore our own dispatch (matches the detail shape in assets/events.js).
  if (event?.detail?.data?.source === SOURCE) return;
  reconcile();
}, 250);

document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
document.addEventListener('DOMContentLoaded', () => reconcile());
if (document.readyState !== 'loading') reconcile();
